import { toStructureArray } from '../utils/mealPlanTargets';
import { eventTimestamp, weightTimestamp } from '../utils/timelineNormalize';
import {
  isTourDemoActive,
  isDemoUserCode,
  isDemoClientId,
  isDemoMealPlanId,
  getDemoProviderId,
  buildDemoClient,
  getDemoMealPlans,
  getDemoMealPlanById,
  getDemoMealPlanStructure,
  getDemoWeightLogs,
  getDemoFoodLogs,
  getDemoConversations,
  getDemoMessages,
} from './tourDemo';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://newdietitianweb-615263253386.europe-west3.run.app/api';

/**
 * Get auth token from the local auth client.
 */
async function getAuthToken() {
  try {
    const { authClient } = await import('../lib/authClient');
    return await authClient.getAccessToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Make API request helper
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    method: options.method || 'GET',
    headers,
    // Prevent the browser from serving stale cached responses (e.g. after editing
    // a meal plan, the next GET /meal-plans/id/:id was returning an old version
    // until site data was cleared manually).
    cache: 'no-store',
  };

  if (options.body) {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      const failure = new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
      failure.status = response.status;
      throw failure;
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

/**
 * Upload a chat image for WhatsApp delivery via dietitian API (service role).
 * Stored in bucket chat-attachments at chat/{userCode}/...
 * @param {string} userCode Client user_code (folder name under chat/)
 * @param {File} file Image file from input
 * @returns {Promise<{ mediaUrl: string, path?: string }>}
 */
export async function uploadWhatsAppChatImage(userCode, file) {
  const imageBase64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

  const token = await getAuthToken();
  if (!token) {
    throw new Error('Not signed in');
  }

  const url = `${API_BASE_URL}/chat/whatsapp-image`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ userCode, imageBase64 }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || data.message || `HTTP error ${response.status}`);
  }
  if (!data.mediaUrl) {
    throw new Error('No media URL returned from server');
  }
  return data;
}

/**
 * Fetch all clients (chat_users)
 * @returns {Promise<Array>} Array of client objects
 */
export async function fetchAllClients() {
  if (isTourDemoActive()) {
    const demo = buildDemoClient(getDemoProviderId());
    let real = [];
    try {
      real = (await apiRequest('/clients')) || [];
    } catch {
      real = [];
    }
    return [demo, ...real.filter((c) => c?.user_code !== demo.user_code)];
  }
  return apiRequest('/clients');
}

/**
 * TopBar clients cache: survives tab switches; cleared on full page refresh.
 * Updated when admin dashboard Refresh is clicked so TopBar stays in sync.
 */
export const topbarClientsCache = {
  clients: [],
};

export function getTopbarClientsCache() {
  return topbarClientsCache.clients || [];
}

/**
 * Update TopBar clients cache (e.g. after admin dashboard refresh) and notify listeners.
 * @param {Array} clients
 */
export function setTopbarClientsCache(clients) {
  topbarClientsCache.clients = Array.isArray(clients) ? clients : [];
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('topbar-clients-updated', { detail: topbarClientsCache.clients }));
  }
}

/**
 * Fetch clients from admin dashboard API (includes engagementPulse for "healthy" / last 24h).
 * Use this for TopBar so ordering and healthy badge stay in sync with dashboard.
 * @returns {Promise<Array>} Array of client objects with engagementPulse
 */
export async function fetchDashboardClients() {
  if (isTourDemoActive()) {
    const demo = buildDemoClient(getDemoProviderId());
    let real = [];
    try {
      const data = await apiRequest('/admin/dashboard');
      real = data.clients || [];
    } catch {
      real = [];
    }
    return [demo, ...real.filter((c) => c?.user_code !== demo.user_code)];
  }
  const data = await apiRequest('/admin/dashboard');
  return data.clients || [];
}

/**
 * Fetch a single client by ID
 * @param {string} clientId - UUID of the client
 * @returns {Promise<Object>} Client object
 */
export async function fetchClientById(clientId) {
  if (isDemoClientId(clientId)) return buildDemoClient(getDemoProviderId());
  return apiRequest(`/clients/${clientId}`);
}

/**
 * Fetch client meal plan configuration
 * @param {string} clientId - UUID of the client
 * @returns {Promise<Object>} Object containing numberOfMeals
 */
export async function fetchClientMealPlanConfig(clientId) {
  if (isDemoClientId(clientId)) return { numberOfMeals: buildDemoClient().number_of_meals };
  return apiRequest(`/clients/${clientId}/meal-plan-config`);
}

/**
 * Fetch client daily targets (calories and macros).
 * Derived from the client record (GET /clients/:id returns the full chat_users row), same shape the
 * old /daily-targets route returned: { calories, protein, carbs, fats, goal, initialWeightKg }.
 * @param {string} clientId - UUID of the client
 * @returns {Promise<Object>}
 */
export async function fetchClientDailyTargets(clientId) {
  // Tour example client: same mapping, from the canned record instead of the network.
  const client = isDemoClientId(clientId)
    ? buildDemoClient(getDemoProviderId())
    : await apiRequest(`/clients/${clientId}`);
  let macros = client?.macros || {};
  if (typeof macros === 'string') {
    try { macros = JSON.parse(macros); } catch { macros = {}; }
  }
  let snapshot = client?.onboarding_targets;
  if (typeof snapshot === 'string') {
    try { snapshot = JSON.parse(snapshot); } catch { snapshot = null; }
  }
  return {
    calories: client?.daily_target_total_calories ?? null,
    protein: macros.protein || macros.protein_g || null,
    carbs: macros.carbs || macros.carbohydrates || macros.carbs_g || null,
    fats: macros.fats || macros.fat || macros.fats_g || null,
    goal: client?.goal || null,
    initialWeightKg: client?.weight_kg || null,
    // Meals per day the client chose at onboarding.
    numberOfMeals: Number(client?.number_of_meals) || null,
    // The targets as onboarding set them, kept by the server the first time a coach changes them.
    // undefined = the column doesn't exist yet; null = nothing changed since onboarding.
    onboardingTargets: client && 'onboarding_targets' in client ? (snapshot || null) : undefined,
    // The client brief: what the meal-plan AI reads besides the targets (raw; the page normalises them).
    brief: client ? {
      foodAllergies: client.food_allergies ?? null,
      foodLimitations: client.food_limitations ?? null,
      medicalConditions: client.medical_conditions ?? null,
      clientPreference: client.client_preference ?? null,
      goal: client.goal ?? null,
      region: client.region ?? null,
      recommendations: client.recommendations ?? null,
    } : null,
    // Profile fields the page can estimate targets from when onboarding never set them.
    profile: client ? {
      age: client.age, date_of_birth: client.date_of_birth, gender: client.gender,
      weight_kg: client.weight_kg, height_cm: client.height_cm,
      Activity_level: client.Activity_level, activity_level: client.activity_level, goal: client.goal,
    } : null,
  };
}

/**
 * Fetch client meal plan structure
 * @param {string} clientId - UUID of the client
 * @returns {Promise<Object>} Meal plan structure object
 */
export async function fetchClientMealPlanStructure(clientId) {
  if (isDemoClientId(clientId)) return getDemoMealPlanStructure();
  return apiRequest(`/clients/${clientId}/meal-plan-structure`);
}

/**
 * Fetch client recommendations
 * @param {string} clientId - UUID of the client
 * @returns {Promise<Object>} Recommendations object
 */
export async function fetchClientRecommendations(clientId) {
  if (isDemoClientId(clientId)) return { recommendations: '' };
  return apiRequest(`/clients/${clientId}/recommendations`);
}

/**
 * Generate meal plan for a client
 * @param {string} clientId - UUID of the client
 * @param {Object} options - Optional parameters for meal plan generation
 * @returns {Promise<Object>} Generated meal plan
 */
export async function generateMealPlan(clientId, options = {}) {
  if (isDemoClientId(clientId)) {
    return getDemoMealPlanById('demo-plan-active');
  }
  return apiRequest('/meal-plans/generate', {
    method: 'POST',
    body: { clientId, ...options },
  });
}

/**
 * Save meal plan for a client
 * @param {string} clientId - UUID of the client
 * @param {Object} mealPlan - The meal plan object to save
 * @returns {Promise<Object>} Saved meal plan
 */
export async function saveMealPlan(clientId, mealPlan) {
  return apiRequest('/meal-plans', {
    method: 'POST',
    body: { clientId, mealPlan },
  });
}

/**
 * Update client configuration (meals, targets, macros)
 * @param {string} clientId - UUID of the client
 * @param {Object} data - Object containing fields to update
 * @returns {Promise<Object>} Updated client object
 */
export async function updateClientSettings(clientId, data) {
  return apiRequest(`/clients/${clientId}/settings`, {
    method: 'PUT',
    body: data,
  });
}

/**
 * Delete a client and all associated data (meal plans, messages, conversations).
 * This action is irreversible.
 * @param {string} clientId - UUID of the client
 * @returns {Promise<Object>} Object with success and message
 */
export async function deleteClient(clientId) {
  return apiRequest(`/clients/${clientId}`, {
    method: 'DELETE',
  });
}

/**
 * Search foods/ingredients
 * @param {string} query - Search query
 * @param {number} limit - Maximum number of results (default: 20, max: 50)
 * @returns {Promise<Object>} Object with data array and error
 */
export async function searchFoods(query, limit = 20) {
  try {
    const url = `${API_BASE_URL}/foods/search?query=${encodeURIComponent(query)}&limit=${limit}`;
    const response = await fetch(url);
    const result = await response.json();

    if (!response.ok) {
      console.error('Error searching foods:', result.error);
      return { data: null, error: { message: result.error } };
    }

    return { data: result.data, error: null };
  } catch (error) {
    console.error('Unexpected error searching foods:', error);
    return { data: null, error };
  }
}

/**
 * Get food by ID
 * @param {string|number} foodId - Food/ingredient ID
 * @returns {Promise<Object>} Object with data and error
 */
export async function getFoodById(foodId) {
  try {
    const url = `${API_BASE_URL}/foods/${foodId}`;
    const response = await fetch(url);
    const result = await response.json();

    if (!response.ok) {
      return { data: null, error: { message: result.error } };
    }

    return { data: result.data, error: null };
  } catch (error) {
    console.error('Error fetching food:', error);
    return { data: null, error };
  }
}

/**
 * Report ingredient issue
 * @param {Object} reportData - Report data object
 * @param {string|number} reportData.foodId - Food ID
 * @param {Object} reportData.foodSnapshot - Snapshot of food data
 * @param {string} reportData.reportType - Type of report (misinformation, incorrect_values, wrong_name, wrong_portion, other)
 * @param {string} reportData.description - Optional description
 * @param {string} reportData.userCode - Optional user code
 * @returns {Promise<Object>} Object with data and error
 */
export async function reportIngredient({ foodId, foodSnapshot, reportType, description, userCode }) {
  try {
    const url = `${API_BASE_URL}/ingredient-reports`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        foodId,
        foodSnapshot,
        reportType,
        description: description || undefined,
        userCode: userCode || undefined,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return { data: null, error: { message: result.error } };
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('Error submitting ingredient report:', error);
    return { data: null, error };
  }
}

/**
 * Fetch conversations for a client
 * @param {string} clientId - UUID of the client (from chat_users)
 * @returns {Promise<Array>} Array of conversation objects
 */
export async function fetchClientConversations(clientId) {
  if (isDemoClientId(clientId)) return getDemoConversations();
  return apiRequest(`/clients/${clientId}/conversations`);
}

/**
 * Fetch messages for a conversation
 * @param {string} conversationId - UUID of the conversation
 * @param {string} before - Optional ISO timestamp for pagination (fetch messages before this date)
 * @returns {Promise<Array>} Array of message objects
 */
export async function fetchConversationMessages(conversationId, before = null) {
  if (isTourDemoActive() && conversationId === 'demo-conversation') {
    return getDemoMessages();
  }
  const url = before 
    ? `/conversations/${conversationId}/messages?before=${encodeURIComponent(before)}`
    : `/conversations/${conversationId}/messages`;
  return apiRequest(url);
}

const EMPTY_TIMELINE_ERRORS = { messages: null, food_logs: null, weight_logs: null };

/**
 * Demo timeline page. Honours `before` and `limit` across all three sources together: it returns
 * only the newest `limit` events older than `before`, so the 5 s poll returns the same events the
 * first load showed (no older rows merged in, no content jump) and "load older" pages backwards.
 */
function demoTimelinePayload({ before = null, limit = 20 } = {}) {
  const cutoff = before ? new Date(before).getTime() : Infinity;
  const tagged = [
    ...getDemoMessages().map((row) => ({ source: 'messages', row, ts: eventTimestamp(row) })),
    ...getDemoFoodLogs().map((row) => ({ source: 'food_logs', row, ts: eventTimestamp(row) })),
    ...getDemoWeightLogs().map((row) => ({ source: 'weight_logs', row, ts: weightTimestamp(row) })),
  ].filter((e) => e.ts && e.ts.getTime() < cutoff);
  tagged.sort((a, b) => b.ts - a.ts);
  const pageSize = limit != null ? Number(limit) : tagged.length;
  const page = tagged.slice(0, pageSize);
  const rest = tagged.slice(pageSize);
  const pick = (source) => page.filter((e) => e.source === source).map((e) => e.row);
  const more = (source) => rest.some((e) => e.source === source);
  return {
    messages: pick('messages'),
    food_logs: pick('food_logs'),
    weight_logs: pick('weight_logs'),
    errors: { ...EMPTY_TIMELINE_ERRORS },
    hasMore: { messages: more('messages'), food_logs: more('food_logs'), weight_logs: more('weight_logs') },
  };
}

/**
 * Fetch one unified timeline page (chat + food + weight). Server omits image_data.
 * Caller must run normalizeTimelineEvents() — do not keep three UI arrays.
 */
export async function fetchClientTimeline({ clientId, userCode = null, before = null, limit = 20 } = {}) {
  if (isDemoClientId(clientId) || isDemoUserCode(userCode)) {
    return demoTimelinePayload({ before, limit });
  }
  const params = new URLSearchParams();
  if (userCode) params.set('userCode', userCode);
  if (before) params.set('before', before);
  if (limit != null) params.set('limit', String(limit));
  return apiRequest(`/clients/${clientId}/timeline?${params.toString()}`);
}

/**
 * Deep food-log payload for the terminal drawer. Never requests image_data.
 */
export async function fetchFoodLogById(id) {
  if (!id) return { data: null, error: { message: 'id is required' } };
  if (String(id).startsWith('demo-food-')) {
    const match = (getDemoFoodLogs() || []).find((log) => String(log.id) === String(id));
    return { data: match || null, error: match ? null : { message: 'Food log not found' } };
  }
  try {
    const result = await apiRequest(`/food-logs/${encodeURIComponent(id)}`);
    return { data: result.data || result, error: null };
  } catch (error) {
    console.error('Error fetching food log detail:', error);
    return { data: null, error };
  }
}

/**
 * Fetch food logs for a client
 * @param {string} userCode - User code of the client
 * @param {string} userId - Optional user ID (alternative to userCode)
 * @param {string} startDate - Optional start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - Optional end date for filtering (YYYY-MM-DD)
 * @returns {Promise<Object>} Object with data array and error
 */
export function getFoodLogImageSrc(log) {
  if (!log?.id) return log?.image_url || null;
  if (log.has_image || log.image_url) {
    return `${API_BASE_URL}/food-logs/${encodeURIComponent(log.id)}/image`;
  }
  return null;
}

export async function fetchFoodLogImageObjectUrl(logId) {
  if (!logId) return null;
  const token = await getAuthToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}/food-logs/${encodeURIComponent(logId)}/image`, {
    method: 'GET',
    headers,
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`Image request failed (${response.status})`);
  const blob = await response.blob();
  if (!blob || blob.size === 0) throw new Error('Empty image');
  return URL.createObjectURL(blob);
}

/**
 * Fetch food logs for a client
 * @param {string} userCode - User code of the client
 * @param {string} userId - Optional user ID (alternative to userCode)
 * @param {string} startDate - Optional start date for filtering (YYYY-MM-DD)
 * @param {string} endDate - Optional end date for filtering (YYYY-MM-DD)
 * @returns {Promise<Object>} Object with data array and error
 */
export async function fetchFoodLogs(userCode = null, userId = null, startDate = null, endDate = null, limit = null, offset = null) {
  if (isDemoUserCode(userCode) || isDemoClientId(userId)) {
    return { data: getDemoFoodLogs(), error: null };
  }
  try {
    let url = '/food-logs?';
    if (userCode) {
      url += `userCode=${encodeURIComponent(userCode)}`;
    } else if (userId) {
      url += `userId=${encodeURIComponent(userId)}`;
    } else {
      return { data: null, error: { message: 'userCode or userId is required' } };
    }
    
    if (startDate) {
      url += `&startDate=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      url += `&endDate=${encodeURIComponent(endDate)}`;
    }
    if (limit !== null) {
      url += `&limit=${encodeURIComponent(limit)}`;
    }
    if (offset !== null) {
      url += `&offset=${encodeURIComponent(offset)}`;
    }

    const result = await apiRequest(url);
    return { data: result.data || [], error: null };
  } catch (error) {
    console.error('Error fetching food logs:', error);
    return { data: null, error };
  }
}

/**
 * Fetch weight logs for a client
 * @param {string} userCode - User code of the client
 * @param {string} userId - Optional user ID (alternative to userCode)
 * @returns {Promise<Object>} Object with data array and error
 */
export async function fetchWeightLogs(userCode = null, userId = null) {
  if (isDemoUserCode(userCode) || isDemoClientId(userId)) {
    // getDemoWeightLogs() is oldest-first; GET /weight-logs is newest-first (measurement_date desc).
    return { data: getDemoWeightLogs().reverse(), error: null };
  }
  try {
    let url = '/weight-logs?';
    if (userCode) {
      url += `userCode=${encodeURIComponent(userCode)}`;
    } else if (userId) {
      url += `userId=${encodeURIComponent(userId)}`;
    } else {
      return { data: null, error: { message: 'userCode or userId is required' } };
    }

    const result = await apiRequest(url);
    return { data: result.data || [], error: null };
  } catch (error) {
    console.error('Error fetching weight logs:', error);
    return { data: null, error };
  }
}

/**
 * Fetch all meal plans for a client by user_code
 * @param {string} userCode - User code of the client
 * @returns {Promise<Array>} Array of meal plan objects
 */
// Saved plans store meal_plan in several shapes over time (object, JSON string, bare list,
// nested { meal_plan: {...} }, meals keyed by slot name). Pages expect { meals: [...], note, totals }.
export function normalizeMealPlanRecord(record) {
  if (!record || typeof record !== 'object') return record;
  let data = record.meal_plan;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch { data = null; }
  }
  if (Array.isArray(data)) data = { meals: data };
  if (data && typeof data === 'object' && !Array.isArray(data.meals)) {
    const nested = data.meal_plan || data.mealPlan || data.menu;
    if (nested && typeof nested === 'object') data = Array.isArray(nested) ? { ...data, meals: nested } : { ...data, ...nested };
    if (!Array.isArray(data.meals)) {
      const meals = toStructureArray(data.meals ?? data);
      if (meals.length) data = { ...data, meals };
    }
  }
  return data ? { ...record, meal_plan: data } : record;
}

export async function fetchClientMealPlans(userCode) {
  if (isDemoUserCode(userCode)) return getDemoMealPlans();
  const plans = await apiRequest(`/meal-plans/${userCode}`);
  return Array.isArray(plans) ? plans.map(normalizeMealPlanRecord) : plans;
}

/**
 * Fetch a single meal plan by ID
 * @param {string} mealPlanId - UUID of the meal plan
 * @returns {Promise<Object>} Meal plan object with full data
 */
export async function fetchMealPlanById(mealPlanId) {
  if (isDemoMealPlanId(mealPlanId)) return getDemoMealPlanById(mealPlanId);
  return normalizeMealPlanRecord(await apiRequest(`/meal-plans/id/${mealPlanId}`));
}

/**
 * Save a new meal plan
 * @param {string} userCode - User code of the client
 * @param {string} mealPlanName - Name of the meal plan
 * @param {Object} mealPlan - The meal plan object
 * @param {number} dailyTotalCalories - Optional daily total calories
 * @param {Object} macrosTarget - Optional macros target object
 * @param {Object} schema - Optional template/schema to save
 * @returns {Promise<Object>} Saved meal plan
 */
export async function saveClientMealPlan(userCode, mealPlanName, mealPlan, dailyTotalCalories = null, macrosTarget = null, schema = null) {
  return apiRequest('/meal-plans', {
    method: 'POST',
    body: {
      userCode,
      mealPlanName,
      mealPlan,
      dailyTotalCalories,
      macrosTarget,
      schema
    }
  });
}

/**
 * Update a meal plan
 * @param {string} mealPlanId - UUID of the meal plan
 * @param {Object} updateData - Object containing fields to update
 * @returns {Promise<Object>} Updated meal plan
 */
export async function updateMealPlan(mealPlanId, updateData) {
  return apiRequest(`/meal-plans/${mealPlanId}`, {
    method: 'PUT',
    body: updateData
  });
}

/**
 * Activate a meal plan with date range and active days
 * @param {string} mealPlanId - UUID of the meal plan
 * @param {string} activeFrom - ISO date string for start date
 * @param {string} activeUntil - ISO date string for end date
 * @param {Array<number>} activeDays - Array of day numbers (0-6, Sunday-Saturday)
 * @returns {Promise<Object>} Updated meal plan
 */
export async function activateMealPlan(mealPlanId, activeFrom, activeUntil, activeDays) {
  return apiRequest(`/meal-plans/${mealPlanId}/activate`, {
    method: 'PUT',
    body: {
      active_from: activeFrom,
      active_until: activeUntil,
      active_days: activeDays
    }
  });
}

/**
 * Deactivate a meal plan and remove from client_meal_plans
 * @param {string} mealPlanId - UUID of the meal plan
 * @returns {Promise<Object>} Updated meal plan
 */
export async function deactivateMealPlan(mealPlanId) {
  return apiRequest(`/meal-plans/${mealPlanId}/deactivate`, {
    method: 'PUT'
  });
}

/**
 * Delete a meal plan. If the plan is active or scheduled, it is removed from client_meal_plans (other project) first.
 * @param {string} mealPlanId - UUID of the meal plan
 * @returns {Promise<Object>} { success, message }
 */
export async function deleteMealPlan(mealPlanId) {
  return apiRequest(`/meal-plans/${mealPlanId}`, {
    method: 'DELETE'
  });
}
/**
 * Meal plan templates shared across the coach's company, newest first.
 * @returns {Promise<Array>} [{ id, meal_plan_name, meal_plan, daily_total_calories, author_name, is_mine, created_at }]
 */
export async function fetchMealPlanTemplates() {
  const templates = await apiRequest('/meal-plans/templates');
  return Array.isArray(templates) ? templates.map(normalizeMealPlanRecord) : [];
}

/**
 * Save a plan as a company template.
 * @param {{ name: string, mealPlan: Object, dailyTotalCalories?: number, macrosTarget?: Object }} template
 */
export async function saveMealPlanTemplate({ name, mealPlan, dailyTotalCalories = null, macrosTarget = null }) {
  return apiRequest('/meal-plans/templates', {
    method: 'POST',
    body: { name, mealPlan, dailyTotalCalories, macrosTarget },
  });
}
